"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { PlanIcon } from "@/components/ui/plan-icon";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
} from "@/components/ui/modal";
import { useSeatInfo, type PlanResponse } from "../hooks/use-subscription";
import { BillingToggle } from "./billing-toggle";
import { PlanCard } from "./plan-card";

const ENTERPRISE_FEATURES = [
  "Custom Credits",
  "SSO/SCIM Support",
  "White Glove Onboarding",
  "Priority Support",
];

interface ChangePlanModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plans: PlanResponse[];
  currentPlanSlug: string | null;
  currentCadence: "annual" | "monthly";
  currentSeatCount: number;
  upgradingSlug: string | null;
  onUpgrade: (planSlug: string, cadence: "annual" | "monthly", seatCount: number) => void;
  onSeatChange: (newCount: number) => void;
  onBreakdownClick?: () => void;
}

export function ChangePlanModal({
  open,
  onOpenChange,
  plans,
  currentPlanSlug,
  currentCadence,
  currentSeatCount,
  upgradingSlug,
  onUpgrade,
  onSeatChange,
  onBreakdownClick,
}: ChangePlanModalProps) {
  const [cadence, setCadence] = useState<"annual" | "monthly">(currentCadence);
  const { data: seatInfo } = useSeatInfo();
  const activeSeats = seatInfo?.active_seats ?? 0;

  const displayPlans = plans.filter((p) => p.slug !== "free");

  const annualSavings = (() => {
    const firstPaid = displayPlans[0];
    if (!firstPaid) return 0;
    const monthly = (firstPaid.price_monthly_cents ?? 0) / 100;
    const annualMonthly =
      Math.round((firstPaid.price_annual_cents ?? 0) / 12) / 100;
    return Math.round((monthly - annualMonthly) * 12);
  })();

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent size="xl">
        <ModalHeader title="Modify plan" iconName="IconLayersTwo" />

        <ModalBody className="flex flex-col gap-5">
          <BillingToggle
            cadence={cadence}
            onCadenceChange={setCadence}
            annualSavings={annualSavings}
          />

          {/* Plan cards */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            {displayPlans.map((plan) => {
              const planOrder: Record<string, number> = { solo: 1, growth: 2, business: 3 };
              const currentRank = currentPlanSlug ? (planOrder[currentPlanSlug] ?? 0) : 0;
              const cardRank = planOrder[plan.slug] ?? 0;
              const isDowngrade = currentRank > 0 && cardRank < currentRank;

              return (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  cadence={cadence}
                  currentCadence={currentCadence}
                  isCurrentPlan={plan.slug === currentPlanSlug}
                  currentSeatCount={currentSeatCount}
                  activeSeats={activeSeats}
                  isDowngrade={isDowngrade}
                  isRecommended={plan.slug === "growth"}
                  isUpgrading={upgradingSlug === plan.slug}
                  onUpgrade={(slug, seatCount) => onUpgrade(slug, cadence, seatCount)}
                  onSeatChange={onSeatChange}
                  onBreakdownClick={onBreakdownClick}
                />
              );
            })}
          </div>

          {/* Enterprise bar */}
          <div className="relative flex flex-col gap-4 overflow-hidden rounded-lg border border-border p-5 md:flex-row md:items-center md:justify-between dark:border-white/10">
            <div
              className="pointer-events-none absolute inset-0 opacity-40 mix-blend-overlay [background-image:linear-gradient(rgba(0,0,0,0.06)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.06)_1px,transparent_1px)] dark:[background-image:linear-gradient(rgba(255,255,255,0.3)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.3)_1px,transparent_1px)]"
              style={{ backgroundSize: "40px 40px" }}
            />
            <div
              className="pointer-events-none absolute inset-x-0 bottom-0 h-full"
              style={{
                background: "linear-gradient(to top, rgba(120, 60, 200, 0.15), transparent)",
              }}
            />

            <div className="relative flex items-center gap-2.5">
              <PlanIcon slug="enterprise" width={24} height={24} />
              <div>
                <span className="text-base font-medium tracking-[-0.48px] text-heading dark:text-white">
                  Enterprise
                </span>
                <p className="text-[13px] text-contrast/60 dark:text-white/70">
                  Make hiring your<br />competitive advantage.
                </p>
              </div>
            </div>

            <div className="relative grid grid-cols-1 gap-x-5 gap-y-2 sm:grid-cols-2">
              {ENTERPRISE_FEATURES.map((feat) => (
                <div key={feat} className="flex items-center gap-1.5">
                  <Icon
                    name="IconCheckCircle2"
                    size={14}
                    className="shrink-0 text-contrast/50 dark:text-white/50"
                  />
                  <span className="whitespace-nowrap text-[13px] tracking-[-0.26px] text-contrast/70 dark:text-white/80">
                    {feat}
                  </span>
                </div>
              ))}
            </div>

            <Button variant="secondary" size="sm" className="relative w-full md:w-auto" asChild>
              <a href="mailto:sales@arbi.ai">Get Started</a>
            </Button>
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
